import { useCallback, useEffect, useState } from 'react';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { isPast } from 'date-fns';
import { useAuth } from '@/lib/auth/auth-context';
import { Database } from '@/types/supabase';

export type SubscriptionStatus = {
  isSubscribed: boolean;
  isLoading: boolean;
  subscription: {
    id: string;
    is_subscribed: boolean | null;
    subscription_end_date: string | null;
    payment_status: string | null;
    vendor_id: string | null;
    subscription_date: string | null;
    payment_method: string | null;
    payment_amount: number | null;
    payment_currency: string | null;
    isExpired: boolean;
    isTrial: boolean;
  } | null;
  checkSubscription: () => Promise<boolean>;
  refreshSubscription: () => Promise<void>;
};

export function useSubscription(): SubscriptionStatus {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus['subscription']>(null);
  const { user } = useAuth();
  const supabase = createClientComponentClient<Database>();

  const checkSubscription = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setIsLoading(false);
      return false;
    }

    try {
      setIsLoading(true);
      
      console.log('[useSubscription] Checking subscription status for user:', user.id);
      
      // Use vendorId from auth context if available, otherwise get it from database
      let vendorId = user.vendorId;
      
      if (!vendorId) {
        // Get user's vendor_id from profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('vendor_id, role')
          .eq('id', user.id)
          .single();
        
        if (profileError) {
          console.error('[useSubscription] Profile query error:', profileError);
          setSubscription(null);
          return false;
        }
        
        vendorId = profileData?.vendor_id || undefined;
        
        // If no vendor_id in profile but user is a vendor, find vendor by user_id
        if (!vendorId && profileData?.role === 'vendor') {
          const { data: vendorData, error: vendorError } = await supabase
            .from('vendors')
            .select('id')
            .eq('user_id', user.id)
            .single();
            
          if (!vendorError && vendorData) {
            vendorId = vendorData.id;
          }
        }
      }
      
      if (!vendorId) {
        console.log('[useSubscription] No vendor_id found for user');
        setSubscription(null);
        return false;
      }
      
      console.log('[useSubscription] Checking subscription for vendor:', vendorId);
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('id, is_subscribed, subscription_end_date, payment_status, vendor_id, subscription_date, payment_method, payment_amount, payment_currency')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error('[useSubscription] Error fetching subscription:', error);
        setSubscription(null);
        return false;
      }

      if (!data) {
        console.log('[useSubscription] No subscription found');
        setSubscription(null);
        return false;
      }

      console.log('[useSubscription] Subscription data:', data);
      
      // Check if subscription is expired
      const isExpired = data.subscription_end_date 
        ? isPast(new Date(data.subscription_end_date)) 
        : true;
      
      const subscriptionData = {
        ...data,
        isExpired,
        isTrial: data.payment_status === 'trial'
      };
      
      setSubscription(subscriptionData);
      
      // A valid subscription is one that has a valid payment status and is not expired
      // Valid payment statuses include 'success', 'captured', and 'trial'
      const validPaymentStatus = ['success', 'captured', 'trial'].includes(data.payment_status || '');
      
      const isActive = 
        (data.is_subscribed === true || validPaymentStatus) && 
        !isExpired;
        
      console.log('[useSubscription] Subscription active:', isActive, 
        'is_subscribed:', data.is_subscribed, 
        'payment_status:', data.payment_status, 
        'isExpired:', isExpired,
        'end_date:', data.subscription_end_date);
      
      return isActive;
    } catch (error) {
      console.error('[useSubscription] Unexpected error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, user?.vendorId, supabase]);

  const refreshSubscription = useCallback(async (): Promise<void> => {
    await checkSubscription();
  }, [checkSubscription]);

  // Reset subscription data when user changes
  useEffect(() => {
    let isMounted = true;
    
    const checkSubscriptionStatus = async () => {
      // When user ID changes (login/logout), reset subscription state
      if (isMounted) setSubscription(null);
      
      // If we have a user, check subscription
      if (user?.id) {
        await checkSubscription();
      } else if (isMounted) {
        setIsLoading(false); // Stop loading if no user
      }
    };
    
    // Execute the async function
    checkSubscriptionStatus();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [user?.id, checkSubscription]); // Include checkSubscription in dependencies

  // Check if payment status is valid (success, captured, or trial)
  const validPaymentStatus = ['success', 'captured', 'trial'].includes(subscription?.payment_status || '');
  
  // Calculate subscription status (only after data is loaded)
  const isSubscribed = !isLoading && (
    (subscription?.is_subscribed === true || validPaymentStatus) && 
    !subscription?.isExpired
  );

  return {
    isSubscribed,
    isLoading,
    subscription,
    checkSubscription,
    refreshSubscription
  };
}
